import { useStore } from '@hooks/useStore'
import { Search } from '@mui/icons-material'
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { IKvkCompany } from '@api'
import { observer } from 'mobx-react-lite'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ISingleClient } from '@typings'

// Context to track KVK-prefilled fields
interface KvkPrefilledContextType {
  prefilledFields: Set<string>
  markFieldAsPrefilled: (fieldName: string) => void
  clearPrefilledFields: () => void
}

export const KvkPrefilledContext = createContext<KvkPrefilledContextType>({
  prefilledFields: new Set(),
  markFieldAsPrefilled: () => {
    // Default no-op implementation
  },
  clearPrefilledFields: () => {
    // Default no-op implementation
  },
})

// Map Dutch country names to English
const COUNTRY_TRANSLATIONS: Record<string, string> = {
  nederland: 'netherlands',
  belgië: 'belgium',
  duitsland: 'germany',
  frankrijk: 'france',
  spanje: 'spain',
  italië: 'italy',
  'verenigd koninkrijk': 'united kingdom',
  'groot-brittannië': 'united kingdom',
  oostenrijk: 'austria',
  zwitserland: 'switzerland',
  polen: 'poland',
  zweden: 'sweden',
  noorwegen: 'norway',
  denemarken: 'denmark',
  finland: 'finland',
  griekenland: 'greece',
  portugal: 'portugal',
  ierland: 'ireland',
  tsjechië: 'czech republic',
  hongarije: 'hungary',
  roemenië: 'romania',
  bulgarije: 'bulgaria',
  kroatië: 'croatia',
  slovakije: 'slovakia',
  slovenië: 'slovenia',
  estland: 'estonia',
  letland: 'latvia',
  litouwen: 'lithuania',
  luxemburg: 'luxembourg',
  malta: 'malta',
  cyprus: 'cyprus',
}

const translateCountryName = (dutchName: string): string => {
  const lowerName = dutchName.toLowerCase().trim()
  return COUNTRY_TRANSLATIONS[lowerName] || lowerName
}

// Hook to access the KVK prefilled context
export const useKvkPrefilled = () => useContext(KvkPrefilledContext)

// Provider component to track prefilled fields
export const KvkPrefilledProvider = ({ children }: { children: React.ReactNode }) => {
  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(new Set())

  const markFieldAsPrefilled = (fieldName: string) => {
    setPrefilledFields(prev => {
      const newSet = new Set(prev).add(fieldName)
      return newSet
    })

    // Auto-clear the highlight after 5 seconds
    setTimeout(() => {
      setPrefilledFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(fieldName)
        return newSet
      })
    }, 5000)
  }

  const clearPrefilledFields = () => {
    setPrefilledFields(new Set())
  }

  return (
    <KvkPrefilledContext.Provider value={{ prefilledFields, markFieldAsPrefilled, clearPrefilledFields }}>
      {children}
    </KvkPrefilledContext.Provider>
  )
}

export const ClientKvkSearch = observer(() => {
  const { kvk, shared } = useStore()
  const { setValue, getValues, reset } = useFormContext<ISingleClient>()
  const { markFieldAsPrefilled } = useKvkPrefilled()
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState<IKvkCompany | null>(null)
  // Track last selected KVK number for race condition protection
  const lastSelectedKvkRef = useRef<string | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue) {
        setSearchQuery(inputValue)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [inputValue])

  // Trigger search when query changes
  useEffect(() => {
    if (searchQuery) {
      kvk.searchCompany(searchQuery)
    }
  }, [searchQuery])

  const applyCountry = (countryName: string | null) => {
    if (!countryName) return

    // Translate Dutch country name to English
    const translatedCountry = translateCountryName(countryName)

    // Try exact match with translated name
    let country = shared.countries.find(c => c.name.toLowerCase() === translatedCountry)

    // If no exact match, try original name
    if (!country) {
      country = shared.countries.find(c => c.name.toLowerCase() === countryName.toLowerCase())
    }

    // If still no match, try partial matching
    if (!country) {
      country = shared.countries.find(c => {
        const nameLower = c.name.toLowerCase()
        return (
          nameLower.includes(translatedCountry) ||
          translatedCountry.includes(nameLower) ||
          nameLower.includes(countryName.toLowerCase()) ||
          countryName.toLowerCase().includes(nameLower)
        )
      })
    }

    if (country) {
      setValue('country', country, { shouldDirty: true })
      markFieldAsPrefilled('country')

      // Force re-render by resetting form with all current values including the new country
      setTimeout(() => {
        const currentValues = getValues()
        reset(currentValues, { keepDirty: true })
      }, 10)
    } else {
      console.log('Country not found:', {
        original: countryName,
        translated: translatedCountry,
        available: shared.countries.map(c => c.name),
      })
    }
  }

  const handleSelectCompany = async (company: IKvkCompany | null) => {
    if (!company) return

    // Track for race condition protection
    lastSelectedKvkRef.current = company.kvk_number

    // Populate form fields from search result
    // Note: We use /zoeken endpoint only (no /basisprofielen call) to avoid €0.02/request cost
    // The /zoeken endpoint already returns complete address data
    if (company.name) {
      setValue('name', company.name, { shouldDirty: true })
      markFieldAsPrefilled('name')
    }

    if (company.street) {
      setValue('street', company.street, { shouldDirty: true })
      markFieldAsPrefilled('street')
    }

    if (company.house_number) {
      setValue('house_number', company.house_number, { shouldDirty: true })
      markFieldAsPrefilled('house_number')
    }

    if (company.house_number_addition) {
      setValue('house_number_addition', company.house_number_addition, { shouldDirty: true })
      markFieldAsPrefilled('house_number_addition')
    }

    if (company.postal_code) {
      setValue('postal_code', company.postal_code, { shouldDirty: true })
      markFieldAsPrefilled('postal_code')
    }

    if (company.city) {
      setValue('city', company.city, { shouldDirty: true })
      markFieldAsPrefilled('city')
    }

    if (company.kvk_number) {
      setValue('coc', company.kvk_number, { shouldDirty: true })
      markFieldAsPrefilled('coc')
    }

    // BTW field NOT populated - KVK does not return BTW numbers
    applyCountry(company.country)

    // Clear search
    setInputValue('')
    setSearchQuery('')
    setSelectedValue(null)
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        options={kvk.list || []}
        value={selectedValue}
        loading={kvk.loading}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Only update if it's an actual input change, not a reset
          if (reason === 'input') {
            setInputValue(newInputValue)
          } else if (reason === 'clear') {
            setInputValue('')
          }
        }}
        onChange={(_, value) => handleSelectCompany(value)}
        getOptionLabel={(option: IKvkCompany) => option.name || ''}
        isOptionEqualToValue={(option: IKvkCompany, value: IKvkCompany) => option.kvk_number === value.kvk_number}
        filterOptions={x => x} // Disable built-in filtering since we search via API
        renderInput={params => (
          <TextField
            {...params}
            label="Zoek bedrijf in KVK (naam of KVK-nummer)"
            placeholder="Typ om te zoeken..."
            InputProps={{
              ...params.InputProps,
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <>
                  {kvk.loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option: IKvkCompany) => (
          <Box
            component="li"
            {...props}>
            <Stack
              spacing={0.5}
              sx={{ width: '100%' }}>
              <Typography
                variant="body1"
                fontWeight={500}>
                {option.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                KVK: {option.kvk_number}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                {option.street} {option.house_number}
                {option.house_number_addition ? ` ${option.house_number_addition}` : ''}, {option.postal_code}{' '}
                {option.city}
              </Typography>
            </Stack>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Typ minimaal 2 karakters om te zoeken'
            : kvk.loading
            ? 'Zoeken...'
            : 'Geen resultaten gevonden'
        }
        clearOnBlur
        clearOnEscape
      />

      {/* Show default helper text when not showing other messages */}
      {(inputValue.length < 2 || kvk.loading) && kvk.list.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}>
          Of vul de gegevens handmatig in onderstaande velden
        </Typography>
      )}
    </Box>
  )
})
